import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Table from '../Theme/Table.js';
import Spinner from '../Spinner';
import ActionButtons from './TransactionsActionButton.js';
import { Button, Modal, Badge } from 'react-bootstrap';
import { IconButton, Tooltip, Chip } from '@mui/material';
import { RestoreFromTrash, Visibility } from '@mui/icons-material';
import moment from 'moment';

const DeletedTripsList = () => {
  const [loading, setLoading] = useState(true);
  const [deletedTrips, setDeletedTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const fetchDeletedTrips = async () => {
    setLoading(true);
    try {
      const response = await axios.get('deletedTrips');
      setDeletedTrips(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching deleted trips:', error);
      toast.error('Failed to load deleted trips');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeletedTrips();
  }, []);

  const handleRestore = async (tripId) => {
    if (!window.confirm('Are you sure you want to restore this trip?')) {
      return;
    }

    try {
      const response = await axios.post(`trips/${tripId}/restore`);
      toast.success(response.data.message || 'Trip restored successfully');
      // Refresh the list
      fetchDeletedTrips();
    } catch (error) {
      console.error('Error restoring trip:', error);
      const errorMessage = error.response?.data?.message || 'Failed to restore trip';
      toast.error(errorMessage);
    }
  };

  const showDetails = (trip) => {
    setSelectedTrip(trip);
    setShowDetailsModal(true);
  };

  const columns = React.useMemo(
    () => [
      {
        Header: 'Trip Info',
        columns: [
          {
            Header: 'ID',
            accessor: 'id',
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ value }) => (
              <Badge bg="danger">Deleted</Badge>
            ),
          },
          {
            Header: 'Vehicle Type',
            accessor: 'vehicleType',
            Cell: ({ value }) => value || 'N/A',
          },
        ],
      },
      {
        Header: 'Passenger',
        columns: [
          {
            Header: 'Name',
            accessor: (row) => {
              return row.Passenger 
                ? `${row.Passenger.firstName || ''} ${row.Passenger.lastName || ''}`.trim()
                : 'N/A';
            },
          },
          {
            Header: 'Phone',
            accessor: 'Passenger.phoneNumber',
            Cell: ({ value }) => value || 'N/A',
          },
        ],
      },
      {
        Header: 'Driver',
        columns: [
          {
            Header: 'Name',
            accessor: (row) => {
              return row.Driver 
                ? `${row.Driver.firstName || ''} ${row.Driver.lastName || ''}`.trim()
                : 'N/A';
            },
          },
          {
            Header: 'Phone',
            accessor: 'Driver.phoneNumber',
            Cell: ({ value }) => value || 'N/A',
          },
        ],
      },
      {
        Header: 'Pricing',
        columns: [
          {
            Header: 'Actual Price',
            accessor: 'actualPrice',
            Cell: ({ value }) => value ? `${value} ETB` : 'N/A',
          },
          {
            Header: 'Revenue',
            accessor: 'revenue',
            Cell: ({ value }) => value ? `${value} ETB` : 'N/A',
          },
        ],
      },
      {
        Header: 'Deletion Info',
        columns: [
          {
            Header: 'Deleted At',
            accessor: 'deletedAt',
            Cell: ({ value }) => value ? moment(value).format('YYYY-MM-DD HH:mm') : 'N/A',
          },
          {
            Header: 'Deleted By',
            accessor: 'deletedBy',
            Cell: ({ value }) => value || 'N/A',
          },
          {
            Header: 'Reason',
            accessor: 'deletionReason',
            Cell: ({ value, row }) => (
              <div style={{ maxWidth: '200px' }}>
                {value ? (
                  <>
                    {value.length > 50 ? `${value.substring(0, 50)}...` : value}
                    {value.length > 50 && (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => showDetails(row.original)}
                        style={{ padding: '0 5px' }}
                      >
                        View Full
                      </Button>
                    )}
                  </>
                ) : 'No reason provided'}
              </div>
            ),
          },
        ],
      },
      {
        Header: 'Actions',
        columns: [
          {
            Header: 'Restore',
            accessor: 'actions',
            Cell: ({ row }) => (
              <div style={{ display: 'flex', gap: '5px' }}>
                <Tooltip title="View Details">
                  <IconButton
                    size="small"
                    color="info"
                    onClick={() => showDetails(row.original)}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Restore Trip">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={() => handleRestore(row.original.id)}
                  >
                    <RestoreFromTrash />
                  </IconButton>
                </Tooltip>
              </div>
            ),
          },
        ],
      },
    ],
    []
  );

  return (
    <div className="content-wrappers" style={{ marginTop: '-40px' }}>
      <div className="content-wrappers">
        <br />
        <br />
        <ToastContainer />

        <section className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <div className="card">
                  <ActionButtons 
                    title="Deleted Trips (Audit Trail)" 
                    showModal={() => {}} 
                  />
                  
                  <div className="card-body">
                    <div className="alert alert-info mb-3" role="alert">
                      <strong>Audit Trail:</strong> This page shows all soft-deleted trips. 
                      These trips are hidden from normal views but can be restored if needed. 
                      All deletion actions are logged for compliance purposes.
                    </div>

                    {loading && (
                      <div>
                        <Spinner />
                      </div>
                    )}
                    {!loading && deletedTrips.length === 0 && (
                      <div className="alert alert-success" role="alert">
                        No deleted trips found. All trips are active.
                      </div>
                    )}
                    {!loading && deletedTrips.length > 0 && (
                      <Table columns={columns} data={deletedTrips} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Details Modal */}
      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Deleted Trip Details - #{selectedTrip?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTrip && (
            <div>
              <h5>Deletion Information</h5>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>Deleted At:</th>
                    <td>{moment(selectedTrip.deletedAt).format('YYYY-MM-DD HH:mm:ss')}</td>
                  </tr>
                  <tr>
                    <th>Deleted By (User ID):</th>
                    <td>{selectedTrip.deletedBy || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Reason:</th>
                    <td>{selectedTrip.deletionReason || 'No reason provided'}</td>
                  </tr>
                </tbody>
              </table>

              <h5 className="mt-4">Trip Information</h5>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <th>Trip ID:</th>
                    <td>{selectedTrip.id}</td>
                  </tr>
                  <tr>
                    <th>Status:</th>
                    <td><Badge bg="danger">Deleted</Badge></td>
                  </tr>
                  <tr>
                    <th>Vehicle Type:</th>
                    <td>{selectedTrip.vehicleType || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Actual Price:</th>
                    <td>{selectedTrip.actualPrice ? `${selectedTrip.actualPrice} ETB` : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Revenue:</th>
                    <td>{selectedTrip.revenue ? `${selectedTrip.revenue} ETB` : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Pick-up Location:</th>
                    <td>{selectedTrip.pickUpPlace || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Destination:</th>
                    <td>{selectedTrip.destinationPlace || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Created At:</th>
                    <td>{moment(selectedTrip.createdAt).format('YYYY-MM-DD HH:mm:ss')}</td>
                  </tr>
                </tbody>
              </table>

              {selectedTrip.Passenger && (
                <>
                  <h5 className="mt-4">Passenger Information</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Name:</th>
                        <td>{`${selectedTrip.Passenger.firstName || ''} ${selectedTrip.Passenger.lastName || ''}`.trim()}</td>
                      </tr>
                      <tr>
                        <th>Phone:</th>
                        <td>{selectedTrip.Passenger.phoneNumber || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}

              {selectedTrip.Driver && (
                <>
                  <h5 className="mt-4">Driver Information</h5>
                  <table className="table table-bordered">
                    <tbody>
                      <tr>
                        <th>Name:</th>
                        <td>{`${selectedTrip.Driver.firstName || ''} ${selectedTrip.Driver.lastName || ''}`.trim()}</td>
                      </tr>
                      <tr>
                        <th>Phone:</th>
                        <td>{selectedTrip.Driver.phoneNumber || 'N/A'}</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
          <Button 
            variant="success" 
            onClick={() => {
              handleRestore(selectedTrip.id);
              setShowDetailsModal(false);
            }}
          >
            <RestoreFromTrash style={{ marginRight: '5px' }} />
            Restore Trip
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DeletedTripsList;
